import type React from 'react'
import { useEffect, useState } from 'react'
import {
  Navigate,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
  useLocation,
} from 'react-router-dom'
import { type IconProps, ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { SimpleDialogContainer } from 'react-simple-dialogs'
import ConditionalServiceWorker from './components/ConditionalServiceWorker'
import OfflineNotificationModal from './components/OfflineNotificationModal'
import PasswordProtection from './components/PasswordProtection'

import { LoadProvider } from './contexts/LoadContext'
import { NotificationProvider } from './contexts/NotificationContext'
import { OfflineProvider, useOffline } from './contexts/OfflineContext'
import { ProjectProvider } from './contexts/ProjectContext'
import { TagProvider } from './contexts/TagContext'
import { UserProvider, useUser } from './contexts/UserContext'
import { WorkOrderProvider } from './contexts/WorkOrderContext'
import AboutPage from './pages/AboutPage'
import AddExpenses from './pages/AddExpenses'
import Auth from './pages/Auth'
import CareersPage from './pages/CareersPage'
import CollaborativeGovernancePage from './pages/CollaborativeGovernancePage'
import CommunityPage from './pages/CommunityPage'
import CompletedConfirmation from './pages/CompletedConfirmation'
import ContactUsPage from './pages/ContactUsPage'
import { ContactsPage } from './pages/ContactsPage'
import CreateBrokerInvoicePage from './pages/CreateBrokerInvoicePage'
import CreateContractorInvoicePage from './pages/CreateContractorInvoicePage'
import CreateDriverInvoicePage from './pages/CreateDriverInvoicePage'
import CreateSubHaulerInvoicePage from './pages/CreateSubHaulerInvoicePage'
import DigitalEcosystemPage from './pages/DigitalEcosystemPage'
import DriverConfirmation from './pages/DriverConfirmation'
import DriverSign from './pages/DriverSign'
import EditInvoicePage from './pages/EditInvoicePage'
import ForgotPasswordForm from './pages/ForgotPasswordForm'
import LandingPage from './pages/LandingPage'
import Layout from './pages/Layout'
import LoadConfirmation from './pages/LoadConfirmation'
import LoadDashboard from './pages/LoadDashboard'
import LoadDetails from './pages/LoadDetails'
import NewsAndPressPage from './pages/NewsAndPressPage'
import PrivateConfirmationPage from './pages/PrivateConfirmationPage'
import ProfilePage from './pages/ProfilePage'
import ProjectsDashboard from './pages/ProjectsDashboard'
import Register from './pages/Register'
import ResetPasswordForm from './pages/ResetPasswordForm'
import SignConfirmation from './pages/SignConfirmation'
import SustainabilityPage from './pages/SustainabilityPage'
import TagDetails from './pages/TagDetails'
import TagReportPage from './pages/TagReportPage'
import TagsDashboard from './pages/TagsDashboard'
import ThirdPartyEngagementsPage from './pages/ThirdPartyEngagementsPage'
import { TrucksPage } from './pages/TrucksPage'
import ViewInvoicePage from './pages/ViewInvoicePage'
import ViewInvoicesPage from './pages/ViewInvoicesPage'
import ViewStatusReportsPage from './pages/ViewStatusReportsPage'
import WorkOrderDashboard from './pages/WorkOrderDashboard'
import { getUserService } from './services/authService'
import { StakeholderType } from './types/user'
import { loadGoogleMapsScript } from './utils/loadGoogleMapsScript'
import './App.css'
import DriverDashboard from './pages/DriverDashboard'

const toastConfig = {
  position: 'top-right' as const,
  autoClose: 3000,
  hideProgressBar: false,
  newestOnTop: false,
  closeOnClick: true,
  rtl: false,
  pauseOnFocusLoss: true,
  draggable: true,
  pauseOnHover: true,
  theme: 'dark' as const,
  icon: ({ theme, type }: IconProps) => {
    switch (type) {
      case 'success':
        return (
          <i
            className={'fa-solid fa-circle-check'}
            style={{ color: '#31C48D' }}
          />
        )
      case 'error':
        return (
          <i
            className={'fa-solid fa-circle-xmark'}
            style={{ color: '#F05252' }}
          />
        )
      case 'warning':
        return (
          <i
            className={'fa-solid fa-triangle-exclamation'}
            style={{ color: '#F59E0B' }}
          />
        )
      case 'info':
        return (
          <i
            className={'fa-solid fa-circle-info'}
            style={{ color: '#3F83F8' }}
          />
        )
      default:
        return (
          <i
            className={'fa-solid fa-circle-check'}
            style={{ color: '#31C48D' }}
          />
        )
    }
  },
  style: {},
  className: 'font-inter',
}

// Component to check if the user is authenticated
const ProtectedRoute = ({ element }: { element: React.ReactNode }) => {
  const { fetchUser } = useUser()
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)

  useEffect(() => {
    const checkAuth = async () => {
      const user = await fetchUser()
      setIsAuthenticated(!!user)
    }
    checkAuth()
  }, [fetchUser])

  if (isAuthenticated === null) {
    // Show a loading indicator while checking authentication
    return (
      <section
        className="bg-slate-900"
        style={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
        }}
      >
        <i className="fa-duotone fa-solid fa-spinner-third fa-spin fa-5x" />
      </section>
    )
  }

  return isAuthenticated ? <>{element}</> : <Navigate to="/login" replace />
}

// Component to protect project routes from drivers only
const ProjectsRoute = ({ element }: { element: React.ReactNode }) => {
  const { user } = useUser()

  // Redirect drivers to tags regardless of stakeholder types
  if (user?.user_type === 'DRIVER') {
    return <Navigate to="/tags" replace />
  }

  // SUB_HAULER, BROKER, and CONTRACTOR can all access project routes to create project terms
  return <>{element}</>
}

// Component to protect work order routes from drivers only
const WorkOrdersRoute = ({ element }: { element: React.ReactNode }) => {
  const { user } = useUser()

  // Redirect drivers to tags regardless of stakeholder types
  if (user?.user_type === 'DRIVER') {
    return <Navigate to="/tags" replace />
  }

  const currentProfile = user?.current_profile?.stakeholder_type
  if (
    currentProfile &&
    currentProfile !== StakeholderType.BROKER &&
    currentProfile !== StakeholderType.SUB_HAULER &&
    currentProfile !== StakeholderType.CONTRACTOR
  ) {
    return <Navigate to="/projects/pending" replace />
  }

  return <>{element}</>
}

// Wrapper to choose Driver vs Non-driver dashboard
const TagsList: React.FC = () => {
  const { user } = useUser()
  if (user?.user_type === 'DRIVER') {
    return <DriverDashboard />
  }
  return <TagsDashboard />
}

// Component to render the offline notification modal
const OfflineNotification: React.FC = () => {
  const { modalVisible, dismissModal } = useOffline()
  return (
    <OfflineNotificationModal isOpen={modalVisible} onDismiss={dismissModal} />
  )
}

const AppContent: React.FC = () => {
  useEffect(() => {
    const handleSync = (event: MessageEvent) => {
      if (event.data && event.data.type === 'SYNC_COMPLETE') {
        console.log('Background sync completed')
        toast.success('Offline changes have been synced', {
          style: { color: '#31C48D' },
        })
      }
    }

    // Register service worker message listener
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.addEventListener('message', handleSync)
      console.log('Registered service worker sync listener')
    }

    // Cleanup listener on unmount
    return () => {
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.removeEventListener('message', handleSync)
      }
    }
  }, [])

  const router = createBrowserRouter(
    createRoutesFromElements(
      <>
        <Route path="/" element={<LandingPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route
          path="/collaborative-governance"
          element={<CollaborativeGovernancePage />}
        />
        <Route path="/digital-ecosystem" element={<DigitalEcosystemPage />} />
        <Route path="/community" element={<CommunityPage />} />
        <Route
          path="/community/sustainability"
          element={<SustainabilityPage />}
        />
        <Route
          path="/community/third-party-engagements"
          element={<ThirdPartyEngagementsPage />}
        />
        <Route path="/news-press" element={<NewsAndPressPage />} />
        <Route path="/careers" element={<CareersPage />} />
        <Route path="/contact" element={<ContactUsPage />} />
        <Route path="/login" element={<Auth />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPasswordForm />} />
        <Route path="/reset-password" element={<ResetPasswordForm />} />
        <Route
          path="/confirmation/private/:confirmationUuid"
          element={<PrivateConfirmationPage />}
        />
        <Route path="/tags" element={<ProtectedRoute element={<Layout />} />}>
          <Route path="/tags" element={<Navigate to="/tags/open" replace />} />
          <Route path="/tags/drafts" element={<TagsList />} />
          <Route path="/tags/completed" element={<TagsList />} />
          <Route
            path="/tags/completed/:tagId/confirmation"
            element={<CompletedConfirmation />}
          />
          <Route path="/tags/open" element={<TagsList />} />
          <Route
            path="/tags/open/:tagId/:loadNumber/details"
            element={<LoadDetails />}
          />
          <Route path="/tags/open/:tagId/loads" element={<LoadDashboard />} />
          <Route
            path="/tags/open/:tagId/loads/confirmation"
            element={<LoadConfirmation />}
          />
          <Route
            path="/tags/open/:tagId/:loads/confirmation/sign"
            element={<SignConfirmation />}
          />
          <Route path="/tags/open/:tagId/expenses" element={<AddExpenses />} />
          <Route
            path="/tags/open/:tagId/confirmation"
            element={<DriverConfirmation />}
          />
          <Route path="/tags/open/:tagId/sign" element={<DriverSign />} />
        </Route>
        <Route
          path="/projects"
          element={
            <ProtectedRoute element={<ProjectsRoute element={<Layout />} />} />
          }
        >
          <Route
            path="/projects"
            element={<Navigate to="/projects/pending" replace />}
          />
          <Route path="/projects/pending" element={<ProjectsDashboard />} />
          <Route path="/projects/accepted" element={<ProjectsDashboard />} />
          <Route path="/projects/declined" element={<ProjectsDashboard />} />
          <Route path="/projects/drafts" element={<ProjectsDashboard />} />
        </Route>
        <Route
          path="/invoices"
          element={<ProtectedRoute element={<Layout />} />}
        >
          <Route
            path="/invoices/create/driver"
            element={<CreateDriverInvoicePage />}
          />
          <Route
            path="/invoices/create/broker"
            element={<CreateBrokerInvoicePage />}
          />
          <Route
            path="/invoices/create/subhauler"
            element={<CreateSubHaulerInvoicePage />}
          />
          <Route
            path="/invoices/create/contractor"
            element={<CreateContractorInvoicePage />}
          />
          <Route path="/invoices/sent" element={<ViewInvoicesPage />} />
          <Route path="/invoices/received" element={<ViewInvoicesPage />} />
          <Route path="/invoices/view/:id" element={<ViewInvoicePage />} />
          <Route path="/invoices/edit/:id" element={<EditInvoicePage />} />
        </Route>
        <Route
          path="/status-reports"
          element={<ProtectedRoute element={<Layout />} />}
        >
          <Route
            path="/status-reports"
            element={<Navigate to="/status-reports/sent" replace />}
          />
          <Route
            path="/status-reports/sent"
            element={<ViewStatusReportsPage />}
          />
          <Route
            path="/status-reports/received"
            element={<ViewStatusReportsPage />}
          />
        </Route>
        <Route
          path="/work-orders"
          element={
            <ProtectedRoute
              element={<WorkOrdersRoute element={<Layout />} />}
            />
          }
        >
          <Route
            path="/work-orders"
            element={<Navigate to="/work-orders/pending" replace />}
          />
          <Route path="/work-orders/drafts" element={<WorkOrderDashboard />} />
          <Route path="/work-orders/pending" element={<WorkOrderDashboard />} />
          <Route
            path="/work-orders/accepted"
            element={<WorkOrderDashboard />}
          />
          <Route
            path="/work-orders/declined"
            element={<WorkOrderDashboard />}
          />
          <Route
            path="/work-orders/completed"
            element={<WorkOrderDashboard />}
          />
          <Route
            path="/work-orders/report/:workOrderId"
            element={<TagReportPage />}
          />
          <Route path="/work-orders/tags/:tagId" element={<TagDetails />} />
        </Route>
        <Route
          path="/tag-report"
          element={<ProtectedRoute element={<Layout />} />}
        >
          <Route path="/tag-report" element={<TagReportPage />} />
          <Route
            path="/tag-report/:statusReportId"
            element={<TagReportPage />}
          />
        </Route>
        <Route
          path="/contacts"
          element={<ProtectedRoute element={<Layout />} />}
        >
          <Route path="/contacts" element={<ContactsPage />} />
        </Route>
        <Route path="/trucks" element={<ProtectedRoute element={<Layout />} />}>
          <Route path="/trucks" element={<TrucksPage />} />
        </Route>
        <Route
          path="/profile"
          element={<ProtectedRoute element={<Layout />} />}
        >
          <Route path="/profile" element={<ProfilePage />} />
        </Route>
      </>,
    ),
  )

  return (
    <>
      <RouterProvider router={router} />
      <ConditionalServiceWorker />
      <ToastContainer {...toastConfig} />
      <SimpleDialogContainer />
    </>
  )
}

// Wrapper component to provide user context to NotificationProvider
const AppWithUserContext: React.FC = () => {
  const { user } = useUser()

  return (
    <NotificationProvider
      currentProfile={user?.current_profile?.stakeholder_type}
      user={user}
    >
      <ProjectProvider>
        <WorkOrderProvider>
          <TagProvider>
            <LoadProvider>
              <AppContent />
            </LoadProvider>
          </TagProvider>
        </WorkOrderProvider>
      </ProjectProvider>
    </NotificationProvider>
  )
}

const App: React.FC = () => {
  useEffect(() => {
    loadGoogleMapsScript()
  }, [])

  return (
    <div className="app-root">
      <OfflineProvider autoDismissTimeout={10000}>
        <PasswordProtection>
          <UserProvider>
            <AppWithUserContext />
          </UserProvider>
        </PasswordProtection>
        <OfflineNotification />
      </OfflineProvider>
    </div>
  )
}

export default App
